import moment from 'moment/min/moment-with-locales';
import 'moment/min/locales';
import format from 'date-fns/format';

import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, Spin, Tooltip, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useCallback, useState } from 'react';

import { DateSearchTypeEnum, IAccount, ICondo, ICondoLocation, ISessionKey } from '@share/common-types';
import { RefundableEnum } from '@common-types';
import { FunctionalRootState, renderNumber, formatDateCheckInOut, getSelectedCurrency, LoginType, isSpanish, getAccountNumberPath, UrlUtils } from '@share/utils';
import { HasPermission, ViewQuotesSharePermission } from '@utils';
import { Currency, ModalShare } from '@components';
import { BlueButton } from '@share/components';
import { CONDO_SESSION_KEY, DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { DownLoadShareType, HandleDownload } from '@share/api';


import './style.scss';
import { enUS, es } from 'date-fns/locale';
import { Routes } from '@share/constants';

const modalWidth = 900;
const percents = 100;
const ONE = 1;
const DATE_FORMAT = 'd MMM';

export interface ModalCompareProps {
  visible: boolean;
  adultsCount: number;
  kidsCount: number;
  bedroomsCount: number;
  isFlexible: boolean;
  months?: string[];
  startDate?: string | null | undefined;
  endDate?: string | null | undefined;
  locationObject: ICondoLocation | null | undefined;
  sessionKey: ISessionKey | null | undefined;
  condos: ICondo[];
  onCancel: () => void;
  onRemove: (condo: ICondo) => void;
}

export function ModalCompare({
  visible,
  adultsCount,
  kidsCount,
  bedroomsCount,
  isFlexible,
  months,
  startDate,
  endDate,
  locationObject,
  sessionKey,
  condos,
  onCancel,
  onRemove,
}: ModalCompareProps) {
  const intl = useIntl();
  const history = useHistory();

  const marginatorStore = useSelector((state: FunctionalRootState) => state.marginatorStore);
  const { account, user } = useSelector((state: FunctionalRootState) => state.loginStore);

  const [loading, setLoading] = useState(false);
  const [visibleAction, setVisibleAction] = useState(false);
  const [isShare, setIsShare] = useState(false);

  const handleView = (condo: ICondo) => {
    const { condoId } = condo;
    const sessionKey = UrlUtils.getUrl(CONDO_SESSION_KEY, condo.sessionKey);
    const accountPath = getAccountNumberPath(history);
    history.push(`${accountPath}${Routes.Condo}/${condoId}?${sessionKey}`);
  };

  const handleData = useCallback(
    (members?: string[], comment?: string) => {
      const value = marginatorStore.value || 0;

      const currency = getSelectedCurrency(account as IAccount);

      const data: Record<string, unknown> = {
        quotes: condos.map((condo) => {
          const marginatorPrice = (condo.price ? condo.price : 0) / (1 - value / 100);
          const savingsNunmber = condo.savings / marginatorPrice * 100;
          const discount = savingsNunmber ? Math.floor(savingsNunmber) : 0;

          return {
            condoId: condo.condoId,
            displayName: condo.displayName,
            fullAddress: condo.fullAddress,
            refundability: condo.refundability,
            reviewsCount: condo.rating?.reviewsCount,
            rating: condo.rating?.value,
            currency,
            price: marginatorPrice,
            publicPrice: condo.publicPrice,
            supplierType: condo.supplierType,
            availableDates: condo.availableDates?.length
              ? [
                  ...condo.availableDates?.map((ad) => ({
                    matchType: ad.matchType,
                    totalNights: moment(ad.to, 'yyyy-MM-DD').diff(moment(ad.from, 'yyyy-MM-DD'), 'days'),
                    from: ad.from,
                    to: ad.to,
                  })),
                ]
              : [],
            discount,
            image: get(condo, 'images[0].url'),
            locationObj: condo.locationObj,
          };
        }),
        comment,
        members,
        location: locationObject,
        searchType: !isFlexible
          ? DateSearchTypeEnum.Strict
          : months?.length
            ? DateSearchTypeEnum.Flexible
            : DateSearchTypeEnum.Anytime,
        condoRequest: {
          location: locationObject,
          adultsCount,
          kidsCount,
          childrenCount: kidsCount,
          bedroomsCount,
        },
        sessionKey,
      };

      if (isFlexible) {
        (data.condoRequest as Record<string, unknown>).months = months?.length
          ? [...months.map((m) => moment(m, 'DD MMM yyyy').format('yyyy-MM-DD'))]
          : [];
      } else {
        (data.condoRequest as Record<string, unknown>).checkIn = startDate;
        (data.condoRequest as Record<string, unknown>).checkOut = endDate;
      }

      return data;
    },
    [
      marginatorStore.value,
      account,
      condos,
      locationObject,
      isFlexible,
      months,
      startDate,
      endDate,
      sessionKey,
      adultsCount,
      kidsCount,
      bedroomsCount,
    ],
  );

  const handleDownload = () => {
    const prevLoading = loading;
    setLoading(true);
    HandleDownload(DownLoadShareType.CONDO, prevLoading, handleData(), intl, () => setLoading(false), () =>
      setVisibleAction(false),
    );
  };

  const currency = getSelectedCurrency(account as IAccount);

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;

  return (
    <Modal
      className={`condo-modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible}
      footer={null}
      onCancel={onCancel}
      wrapClassName="condo-modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="condo-modal-compare__title-row">
          <h4 className="condo-modal-compare__title secondary-font">
            <FormattedMessage id="results.condos.card.compare.modal.title" />
          </h4>
        </div>

        <div className="condo-modal-compare__hotels condo-modal-compare__hotels--meta">
          <div className="hotels-dates">
            {isFlexible ? (
              <>
                <FormattedMessage id="flexible.dates" /> -{' '}
                {months?.length ? months?.map((m) => moment(m, 'DD MMM yyyy').format('MMMM')).join(', ') : <FormattedMessage id="anytime" />}
              </>
            ) : (
              <>
                <FormattedMessage id="specific.dates" /> -{' '}
                {formatDateCheckInOut(startDate as string, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} -{' '}
                {formatDateCheckInOut(endDate as string, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)}
              </>
            )}
          </div>
          <div className="hotels-dates">
            {adultsCount} <FormattedMessage id="adult" values={{ count: adultsCount }} /> - {kidsCount}{' '}
            <FormattedMessage id="child" values={{ count: kidsCount }} /> - {bedroomsCount}{' '}
            <FormattedMessage id="bedroom" values={{ count: bedroomsCount }} />
          </div>
        </div>

        <div className="condo-modal-compare__hotels">
          {condos.map((condo, index) => {
            const { price, availableDates } = condo;

            const value = marginatorStore.value || 0;
            const marginatorPrice = (price ? price : 0) / (1 - value / 100);

            const msave = condo.publicPrice ? (marginatorPrice * percents) / condo.publicPrice : 100;
            const savings = Math.floor(percents - msave);

            return (
              <div key={index} className="condo-modal-compare__hotel">
                <div className="condo-modal-compare__media">
                  <div className="condo-images-container">
                    <div className="condo-image" style={{ backgroundImage: `url(${get(condo, 'images[0].url')})` }} />
                    {savings > 0 ? (
                      <div className="condo-modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                  <label
                    className={`condo-modal-compare__remove-btn ${condos.length <= 2 ? 'disabled' : ''}`}
                    onClick={() => {
                      if (condos.length > 2) {
                        onRemove(condo);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} className="condo-modal-compare__remove-btn-icon" />
                  </label>
                </div>

                <div className="condo-text-container condo-text-container--title">
                  <Tooltip title={condo.displayName} placement="top">
                    <p>{condo.displayName}</p>
                  </Tooltip>
                </div>

                <div className="condo-text-container condo-text-container--address">
                  <Tooltip title={condo.fullAddress ?? ''} placement="top">
                    <p>{condo.fullAddress}</p>
                  </Tooltip>
                </div>

                <div className="condo-text-container" style={{ height: isSpanish() ? '60px' : '40px' }}>
                  {availableDates?.length ? (
                    <div className="condo-modal-compare__date-label">
                      {availableDates.length >= ONE && (
                        <div className="condo-modal-compare__date-more">
                          <p className="condo-modal-compare__date-more-text">
                            <Tooltip
                              className="condo-compare-tooltip"
                              overlayStyle={{ whiteSpace: 'pre-line' }}
                              placement="top"
                              title={
                                <>
                                  {availableDates.map((d, i) => (
                                    <Typography key={i} style={{ color: '#FFFFFF' }}>
                                      {format(new Date(d.from), DATE_FORMAT, { locale: isSpanish() ? es : enUS })} -{' '}
                                      {format(new Date(d.to), DATE_FORMAT, { locale: isSpanish() ? es : enUS })}
                                    </Typography>
                                  ))}
                                </>
                              }
                            >
                              {availableDates.length} <FormattedMessage id="available.dates" />
                            </Tooltip>
                          </p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {condo.refundability && condo.refundability !== RefundableEnum.Unknown ? (
                  <div className="condo-text-container condo-text-container--refundable">
                    <div className="hotel-card-wrapper__refundable">
                      {condo.refundability === RefundableEnum.Refundable && (
                        <div className="hotel-card-wrapper__refundable-ref">
                          <FormattedMessage id="refundable" />
                        </div>
                      )}
                      {condo.refundability === RefundableEnum.Nonrefundable && (
                        <div className="hotel-card-wrapper__refundable-nonref">
                          <FormattedMessage id="non.refundable" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="condo-amenities-container" style={{ height: isSpanish() ? '60px' : '43px' }}>
                  {condo.rating?.value || condo.rating?.reviewsCount ? (
                    <label>
                      {condo.rating?.value ? (
                        <>
                          <strong>{condo.rating?.value.toFixed(1)}</strong> / 5
                        </>
                      ) : null}{' '}
                      {condo.rating?.reviewsCount ? (
                        <>
                          ({condo.rating?.reviewsCount} <FormattedMessage id="reviews" />)
                        </>
                      ) : null}
                    </label>
                  ) : (
                    <label>
                      <FormattedMessage id="reviews.guest.no.ratings" />
                    </label>
                  )}
                </div>

                <div className="condo-text-container condo-text-container--price">
                  {condo.publicPrice ? (
                    <p style={{ textDecoration: 'line-through' }}>
                      <Currency currency={currency} />
                      {renderNumber(condo.publicPrice, 2)}
                    </p>
                  ) : null}
                  <p>
                    <strong>
                      <Currency currency={currency} />
                      {renderNumber(marginatorPrice, 2)}
                    </strong>
                  </p>
                </div>

                <div className="condo-view-container">
                  <div className="view-button" onClick={() => handleView(condo)}>
                    <FormattedMessage id="results.hotel.card.compare.modal.view" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="condo-modal-compare__buttons">
          {!account || account.type !== LoginType.PrivateWithToken ? (
            account?.displayShareCompareCondos && (
              <BlueButton disabled={loading} onClick={handleDownload}>
                {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
              </BlueButton>
            )
          ) : (
            <>
              {account?.displayShareCompareCondos && (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(false);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
                </BlueButton>
              )}

              {HasPermission(user, ViewQuotesSharePermission) ? (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(true);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.share" />}
                </BlueButton>
              ) : null}
            </>
          )}
        </div>
      </div>

      <ModalShare
        visible={visibleAction}
        isShare={isShare}
        type={DownLoadShareType.CONDO}
        onCancel={() => setVisibleAction(false)}
        onLoading={(v) => setLoading(v)}
        convertData={handleData}
      />
    </Modal>
  );
}
